/**
 * The staff session — IN MEMORY, deliberately.
 *
 * The referee surface uses a cookie session because a referee follows an emailed link on a phone
 * and must survive a reload. Staff do not have that constraint, and this surface renders the most
 * sensitive data in the product, so the token lives in a module-scoped store and nowhere else:
 *
 *   - NOT localStorage/sessionStorage — readable by any script that reaches this origin, and it
 *     survives the tab, so a shared or unlocked machine keeps a working staff token indefinitely.
 *   - NOT a cookie — the admin rail is a scripted client on its own origin with no cookie session
 *     to ride, and a cookie is attached to requests we did not write.
 *
 * The cost is real and accepted: a reload signs you out. That is the correct trade for a console
 * whose session can reveal a referee's private words.
 *
 * This store is the ONLY writer of the token. `getAccessToken` below is what the staffClient's
 * per-request provider calls, so a rotated token is picked up without rebuilding the client.
 */
import { create } from 'zustand';
import type { StaffTokens } from '@/lib/wire';

export interface StaffSessionState {
  accessToken: string | null;
  /** ISO-8601. Drives proactive rotation; see `tokenProvider` in lib/admin-api.ts. */
  accessTokenExpiresAt: string | null;
  refreshToken: string | null;
  /** Present from /otp/verify; the /refresh response does not re-state it (staffId: null). */
  staffId: string | null;
  signIn: (tokens: StaffTokens) => void;
  /** Replace the pair after a rotation, preserving the staff id the refresh response omits. */
  rotate: (tokens: StaffTokens) => void;
  signOut: () => void;
}

const SIGNED_OUT = {
  accessToken: null,
  accessTokenExpiresAt: null,
  refreshToken: null,
  staffId: null,
} as const;

export const useStaffSession = create<StaffSessionState>((set) => ({
  ...SIGNED_OUT,
  signIn: (tokens) =>
    set({
      accessToken: tokens.accessToken,
      accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      refreshToken: tokens.refreshToken,
      staffId: tokens.staffId,
    }),
  rotate: (tokens) =>
    set((state) => ({
      accessToken: tokens.accessToken,
      accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      refreshToken: tokens.refreshToken,
      // /refresh returns staffId: null by design — it is not re-stating identity, just rotating
      // the pair. Keep what sign-in established rather than blanking it.
      staffId: tokens.staffId ?? state.staffId,
    })),
  signOut: () => set({ ...SIGNED_OUT }),
}));

/** Read the current access token without subscribing (for the client's per-request provider). */
export function currentAccessToken(): string | null {
  return useStaffSession.getState().accessToken;
}

/** True when a staff token is held. The single predicate the auth guard reads. */
export function isSignedIn(): boolean {
  return currentAccessToken() !== null;
}

/** Drop the session. Called on sign-out and whenever the rail answers 401. */
export function signOut(): void {
  useStaffSession.getState().signOut();
}
